import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2 } from "lucide-react";

export default function TeamInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(undefined);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(null); return; }
    api.get(`/company/team/invite-info/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company/team/accept", { token, name, password });
      localStorage.setItem("ta_token", data.access_token);
      setUser(data.user);
      toast.success("Welcome to the team!");
      nav("/employer");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (info === undefined) return <div className="min-h-screen grid place-items-center text-muted font-mono text-sm">Loading invite…</div>;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-surface border-r border-white/10">
        <Logo />
        <div>
          <h1 className="font-display font-bold text-4xl text-ink leading-tight">Join your team's<br />hiring workspace.</h1>
          <p className="mt-4 text-muted text-base max-w-sm">Review pre-vetted global technical talent together — shared searches, saved candidates and conversations.</p>
        </div>
        <p className="label-mono text-faint">TEAMASSEMBLY.IO</p>
      </div>

      <div className="grid place-items-center p-6">
        <div className="w-full max-w-sm" data-testid="team-invite-page">
          {!info ? (
            <div className="text-center" data-testid="invite-invalid">
              <Users className="mx-auto text-faint mb-4" size={30} />
              <h2 className="font-display font-bold text-2xl text-ink">Invite unavailable</h2>
              <p className="mt-2 text-muted text-sm">This invite link is invalid, has expired, or was already used. Ask the workspace owner to send a new invite.</p>
              <button onClick={() => nav("/login")} className="mt-6 rounded-full border border-white/15 px-5 py-2.5 text-sm text-ink hover:bg-white/5 transition-colors duration-200" data-testid="go-login-btn">Go to sign in</button>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald/10 px-3 py-1 text-[11px] text-emerald mb-4"><CheckCircle2 size={12} /> You've been invited</div>
              <h2 className="font-display font-bold text-2xl text-ink">Join {info.company_name}</h2>
              <p className="mt-1 text-muted text-sm">Set up your account for <span className="text-ink font-mono">{info.email}</span> to join the shared workspace.</p>

              <label className="label-mono text-faint block mt-6 mb-1">Full name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input"
                className="w-full rounded-lg border border-white/12 bg-base px-3 py-2.5 text-sm text-ink focus:border-azure focus:outline-none transition-colors duration-200" />

              <label className="label-mono text-faint block mt-4 mb-1">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input"
                className="w-full rounded-lg border border-white/12 bg-base px-3 py-2.5 text-sm text-ink focus:border-azure focus:outline-none transition-colors duration-200" />
              <p className="mt-1.5 text-[11px] text-faint">At least 12 characters, with an uppercase, lowercase, number and a symbol (! @ # $).</p>

              <button onClick={accept} disabled={busy || !name.trim() || !password} data-testid="accept-invite-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-azure px-6 py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : null} Accept & join team
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
